import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { applicationApi } from "api/application";
import type { Application } from "types/application";
import { getErrorMessage } from "utils/error";
import { ApplicationStagePicker } from "./ApplicationStagePicker";
import { useApplicationStages } from "./useApplicationStages";

export const ApplicationStageSelect = ({
  application,
  placement
}: {
  application: Pick<Application, "id" | "stage" | "stage_changed">;
  placement?: "bottom-start" | "bottom-end";
}) => {
  const queryClient = useQueryClient();
  const { data: stages = [] } = useApplicationStages();
  const savedStageId = application.stage?.id ?? null;
  const [pickedStageId, setPickedStageId] = useState<number | null>();

  useEffect(() => {
    setPickedStageId(undefined);
  }, [savedStageId]);

  const changeStage = useMutation({
    mutationKey: ["APPLICATION_STAGE", "UPDATE"],
    mutationFn: (stageId: number | null) =>
      applicationApi.bulkChangeStage({
        application_ids: [application.id],
        stage_id: stageId
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["APPLICANTS_TABLE"] });
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION_FILTER_OPTIONS"]
      });
      queryClient.invalidateQueries({ queryKey: ["APPLICATION_ACTIVITY_LOG"] });
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", String(application.id)]
      });
    },
    onError: (error) => {
      setPickedStageId(undefined);
      toast.error(`Failed to change stage: ${getErrorMessage(error)}`);
    }
  });

  return (
    <ApplicationStagePicker
      value={pickedStageId === undefined ? savedStageId : pickedStageId}
      changed={pickedStageId === undefined ? application.stage_changed : null}
      stages={stages}
      placement={placement}
      disabled={changeStage.isPending}
      onChange={(stageId) => {
        if (changeStage.isPending) return;
        setPickedStageId(stageId);
        changeStage.mutate(stageId);
      }}
    />
  );
};
